import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { CapService, IPaymentData } from '../api';

interface IUseCapStatusResult {
  sessionId: string | null;
  checkoutId: string | null;
  paymentData: IPaymentData | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Custom hook for CAP payment status page
 * Handles payment status loading from callback parameters or props
 */
export const useCapStatus = (
  propSessionId?: string,
  propCheckoutId?: string,
  propEnv?: string
): IUseCapStatusResult => {
  const location = useLocation();
  const [paymentData, setPaymentData] = useState<IPaymentData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadAttemptedRef = useRef(false);

  // Get params from URL query or props
  const { sessionId, checkoutId, env } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    // Try to get env from: props > URL params > localStorage
    const envFromStorage = localStorage.getItem('cap_env');
    const finalEnv = propEnv || params.get('env') || envFromStorage;
    
    console.log('Status page env resolution:', {
      propEnv,
      urlEnv: params.get('env'),
      storageEnv: envFromStorage,
      finalEnv
    });

    return {
      sessionId: propSessionId || params.get('sessionId'),
      checkoutId: propCheckoutId || params.get('id'),
      env: finalEnv || null,
    };
  }, [location.search, propSessionId, propCheckoutId, propEnv]);

  const capService = useMemo(() => {
    // If no env, will be handled in loadPaymentStatus
    return env ? new CapService(env) : null;
  }, [env]);

  /**
   * Load payment status from API
   */
  const loadPaymentStatus = useCallback(async () => {
    if (!env) {
      setError('env parameter is required. Unable to determine environment from URL or localStorage.');
      setIsLoading(false);
      return;
    }
    if (!sessionId || !checkoutId) {
      setError('sessionId and checkoutId are required in URL');
      setIsLoading(false);
      return;
    }

    if (!capService) {
      setError('Failed to initialize CAP service');
      setIsLoading(false);
      return;
    }

    if (loadAttemptedRef.current) {
      console.log('Load already attempted, skipping duplicate request');
      return;
    }

    loadAttemptedRef.current = true;

    try {
      console.log('Loading payment status for:', { sessionId, checkoutId, env });
      const data = await capService.getPaymentStatus(sessionId, checkoutId);
      console.log('Payment status loaded:', data);
      setPaymentData(data);
      setError(null);
      
      // Clear env from localStorage after successful load
      localStorage.removeItem('cap_env');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load payment status';
      setError(errorMessage);
      console.error('Failed to load payment status:', err);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, checkoutId, env, capService]);

  // Load payment status on mount
  useEffect(() => {
    loadPaymentStatus();
  }, [loadPaymentStatus]);

  return {
    sessionId,
    checkoutId,
    paymentData,
    isLoading,
    error,
  };
};

